import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

import { TaskService } from '../../../../../core/timeline';
import { UserService } from '../../../../../core/auth/_services';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';

@Component({
  selector: 'kt-task-history',
  templateUrl: './task-history.component.html',
  styleUrls: ['./task-history.component.scss'],
})
export class TaskHistoryComponent implements OnInit {
  private usersMap = {};
  private entitiesMap = {};
  private officesMap = {};
  loadingHistory = false;
  history = [];
  @Input() taskId: number;
  @Input() stepId: number;
  @Input() version: number;
  @Input() showHeader = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { taskId: number; stepId: number; version?: number },
    private dialogRef: MatDialogRef<TaskHistoryComponent>,
    private taskService: TaskService,
    private userService: UserService,
    private stringUtils: StringUtilsService,
  ) {}

  ngOnInit(): void {
    let taskHistory = [];
    let taskCommentsHistory = [];
    const newTaskHistory = [];

    this.loadingHistory = true;
    this.taskId = this.taskId || this.data.taskId;
    this.stepId = this.stepId || this.data.stepId;
    this.version = this.version || this.data.version;

    if (!this.taskId) {
      this.history = [];
      this.loadingHistory = false;

      return;
    }

    const getTaskHistory$ = this.stepId
      ? this.taskService.getTaskV3History(this.taskId, this.stepId, this.version)
      : of({ data: [] });

    getTaskHistory$.pipe(take(1)).subscribe((res1) => {
      if (!res1?.data?.length) return;

      taskHistory = res1.data;
      taskCommentsHistory = [];

      let userIds = [];
      let entityIds = [];
      let officeIds = [];
      const taskHistoryLength = taskHistory.length;

      taskHistory.forEach((th) => {
        userIds.push(...th.task_followers?.map((f) => f.follower_id));
        userIds.push(...th.task_responsible_users?.map((u) => u.responsible_id));
        // entityIds.push(...th.task_entities?.map(e => e.entity_id));
        if (th.office_id) officeIds.push(th.office_id);
      });

      userIds = [...new Set(userIds)];
      entityIds = [...new Set(entityIds)];
      officeIds = [...new Set(officeIds)];

      forkJoin([
        userIds.length ? this.taskService.getUsersbyId(userIds) : of(null),
        entityIds.length ? this.taskService.getEntities(entityIds) : of(null),
        officeIds.length ? this.userService.getAllOffices(officeIds) : of(null),
      ])
        .pipe(take(1))
        .subscribe(
          (res2) => {
            if (!res2?.length) return;

            if (res2[0]?.pageOfItems?.length)
              res2[0].pageOfItems.forEach((u) => (this.usersMap[u.id] = u.fullName.trim()));
            if (res2[1]?.data?.length)
              res2[1].data.forEach(
                (e) =>
                  (this.entitiesMap[e.id] = `${e.first_name || ''} ${
                    e.last_name || ''
                  } (${this.stringUtils.formatEnumString(e.entity_type)})`.trim()),
              );
            if (res2[2]?.length) res2[2].forEach((o) => (this.officesMap[o.id] = o.name.trim()));

            taskHistory.forEach((th, idx) => {
              switch (th.rev_type) {
                case 'ADD':
                  newTaskHistory.push({
                    property: 'Task Created',
                    old: '',
                    new: '',
                    changedBy: th.username,
                    dateTime: th.rev_timestamp,
                  });
                  break;

                case 'MOD': {
                  const current = this.getTaskSubset(th);
                  const previous =
                    idx + 1 < taskHistoryLength ? this.getTaskSubset(taskHistory[idx + 1]) : null;

                  Object.keys(current)?.forEach((key) => {
                    if (
                      previous &&
                      JSON.stringify(current[key]) !== JSON.stringify(previous[key])
                    ) {
                      newTaskHistory.push({
                        property: this.stringUtils.formatEnumString(key),
                        old: this.formatTaskPropValue(key, previous[key]),
                        new: this.formatTaskPropValue(key, current[key]),
                        changedBy: th.username,
                        dateTime: th.rev_timestamp,
                      });
                    }
                  });
                  break;
                }
                case 'DEL':
                  newTaskHistory.push({
                    property: 'Task Deleted',
                    old: '',
                    new: '',
                    changedBy: th.username,
                    dateTime: th.rev_timestamp,
                  });
                  break;
              }
            });

            taskCommentsHistory = taskCommentsHistory.map((tch, idx) => {
              switch (tch.rev_type) {
                case 'ADD':
                  return {
                    property: 'Comment Added',
                    old: '',
                    new: tch.comment,
                    changedBy: tch.last_modified_user,
                    dateTime: tch.rev_timestamp,
                  };

                case 'MOD':
                  return {
                    property: 'Comment Modified',
                    old: taskCommentsHistory[idx + 1]?.comment,
                    new: tch.comment,
                    changedBy: tch.last_modified_user,
                    dateTime: tch.rev_timestamp,
                  };

                case 'DEL':
                  return {
                    property: 'Comment Deleted',
                    old: tch.comment,
                    new: '',
                    changedBy: tch.last_modified_user,
                    dateTime: tch.rev_timestamp,
                  };
              }
            });

            this.history = [...newTaskHistory, ...taskCommentsHistory].sort((a, b) =>
              a.dateTime > b.dateTime ? -1 : 1,
            );
            this.loadingHistory = false;
          },
          () => {
            this.loadingHistory = false;
          },
        );
    });
  }

  getTaskSubset(task) {
    return {
      title: task.title,
      due_date: task.due_date,
      priority: task.priority,
      progress: task.progress,
      office_id: task.office_id,
      description: task.description,
      url_resource: task.url_resource,
      // task_attachments: task.task_attachments?.sort((a, b) => a.id < b.id ? -1 : 1),
      task_tags: task.task_tags?.map((t) => t.tag).sort((a, b) => (a.id < b.id ? -1 : 1)),
      // task_entities: task.task_entities?.map(e => e.entity_id).sort((a, b) => a < b ? -1 : 1),
      task_followers: task.task_followers
        ?.map((f) => f.follower_id)
        .sort((a, b) => (a < b ? -1 : 1)),
      task_responsible_users: task.task_responsible_users
        ?.map((u) => u.responsible_id)
        .sort((a, b) => (a < b ? -1 : 1)),
    };
  }

  formatTaskPropValue(prop, value) {
    switch (prop) {
      case 'priority':
      case 'progress':
        return this.stringUtils.formatEnumString(value);

      case 'task_attachments':
        return value.map((a) => a.path.split('/').pop()).join(', ');

      case 'task_entities':
        return value.map((e) => this.entitiesMap[e]).join(', ');

      case 'task_followers':
      case 'task_responsible_users':
        return value.map((u) => this.usersMap[u]).join(', ');

      case 'task_tags':
        return value.map((t) => t.name).join(', ');

      case 'office_id':
        return this.officesMap[value];

      default:
        return value;
    }
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }
}
